import Color from '@/Enums/Color.enum';
import type { IProduct } from '@/Interfaces/Product.interface';
import { NEW_ITEM_ID } from '@/constants/campaignManagement';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsProductSelectionCardHidden } from '@/store/selectors/currentWhitelabel.selectors';
import { selectDCV360TicketsProducts } from '@/store/selectors/dv360Tickets/insertionOrders.selectors';
import { getDV360TicketsProducts } from '@/store/slices/dv360Tickets/insertionOrderManagement.slice';
import { Box, FormLabel } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

const DV360TicketsProductSection = () => {
  const dispatch = useAppDispatch();
  const { control, setValue } = useFormContext();

  const { products } = useAppSelector(selectDCV360TicketsProducts);
  const isProductSelectionCardHidden = useAppSelector(selectIsProductSelectionCardHidden);

  const [watchId, watchProduct]: [string, IProduct | null] = useWatch({
    control,
    name: ['id', 'product'],
  });

  const isProductSelectionDisabled = useMemo(() => watchId !== NEW_ITEM_ID, [watchId]);

  const handleProductSelection = useCallback(
    ({ id, name, rateCardCpm, videoDuration }: IProduct) => {
      setValue('product', { id, name, rateCardCpm, videoDuration }, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  useEffect(() => {
    if (!products?.length) {
      dispatch(getDV360TicketsProducts());
    }
  }, [dispatch, products]);

  if (isProductSelectionCardHidden) {
    return null;
  }

  return (
    <div className='flex flex-col mt-2'>
      <div className='flex justify-between items-end'>
        <FormLabel aria-label='Product' required={!isProductSelectionDisabled}>
          Product
        </FormLabel>
      </div>
      <div className={classNames('flex flex-col border border-[#E8E8E8] rounded-md')}>
        <div className='w-full flex bg-[#E8E8E8]/40 rounded-t-lg'>
          <p className={`w-full px-4 py-2 border-r font-[Manrope] text-[${Color.Grey}] text-[14px] font-semibold`}>
            Product
          </p>
        </div>
        <div className='flex h-96'>
          <Box
            className={classNames('flex flex-col w-full overflow-y-auto p-2 border-r', {
              'bg-[#E8E8E8]/40': isProductSelectionDisabled,
            })}
          >
            {!!products?.length &&
              products.map((product: IProduct) => (
                <Box
                  key={product.id}
                  component='button'
                  type='button'
                  className={classNames(
                    'w-full text-start px-3 py-1 rounded-md font-[Manrope] text-[16px] text-[#303030] focus:outline-none',
                    {
                      'bg-[#1b43321f]': watchProduct?.id === product.id,
                      'cursor-not-allowed': isProductSelectionDisabled,
                    },
                  )}
                  onClick={() => handleProductSelection(product)}
                  disabled={isProductSelectionDisabled}
                  aria-label={product.name}
                >
                  {product.name}
                </Box>
              ))}
          </Box>
        </div>
      </div>
    </div>
  );
};

export default DV360TicketsProductSection;
